import React, { type ReactNode, useContext, useEffect, useState } from 'react';
import type { FormField } from '@firsttable/types';
import { useRouter } from 'next/router';
import { useAlert } from 'react-alert';
import * as yup from 'yup';
import { useQuery, gql } from '@apollo/client';
import { CREATE_USER, LOGIN_QUERY, formFieldFragment } from '@firsttable/graphql-queries';
import { Link } from '@firsttable/web-components/atoms';
import InputField from '@firsttable/web-components/molecules/InputField/InputFieldWithButton';
import { FormContext } from '@/components/context/FormContext/FormContext';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import Button from '@/components/analytics/components/Button';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import EmailSummary from '@/components/molecules/EmailSummary';
import NavLinkComponent from '@/components/GatsbyLinkConvertor';
import { useSuburbPreferenceSignupFields } from '@/components/analytics/hooks/useSuburbPreferenceSignupStep';

const CHECK_EMAIL = gql`
  query checkEmail($email: String!) {
    memberSignInMethods(email: $email)
  }
`;

const GET_NEW_MEMBER_FIELDS = gql`
  ${formFieldFragment}
  query newMemberFields($siteId: Int!, $email: String) {
    SignUpFields(siteId: $siteId, email: $email) {
      ...formFieldFragment
    }
  }
`;

const SIGNUP_FIELDS_ERROR_MESSAGE =
  "We couldn't load the sign-up form. Please press Next to try again.";

const schema = yup.object().shape({
  email: yup.string().required().email(),
});

const ForgotPasswordLink = ({ email }: { email?: string }) => (
  <TrackProvider link_name="forgot password">
    <Link
      NavComponent={NavLinkComponent}
      to={`/auth/forgot-password/?email=${encodeURIComponent(email)}`}
      color="brand.red"
      underline>
      Forgot password?
    </Link>
  </TrackProvider>
);

export type EmailCheckFieldProps = {
  email?: string | null;
  error?: ReactNode | string | boolean | null;
  id?: string | null;
  label?: string | null;
  name: string;
  placeholder?: string | null;
  nextButtonKind?: 'cta' | 'normal';
  // Shapes the new-account fields before they enter form state. Existing-account sign-in fields stay as built below, so a booking-page presentation change cannot drop the password step.
  transformSignupFields?: (fields: FormField[]) => FormField[];
  // Collapses the input into a read-only summary once the diner moves past the email step, matching the multi-step form on `/join`.
  summariseWhenActive?: boolean;
  summaryClassName?: string;
  setStatus: (status: { touched: any[]; error: any[] }) => void;
  success?: string | null;
  touched?: boolean | null;
  formState: {
    extraInitialValues: object;
  };
};

export default function EmailCheckField({
  label,
  id,
  name,
  placeholder,
  nextButtonKind = 'cta',
  summariseWhenActive = false,
  summaryClassName,
  transformSignupFields,
  success,
  error,
  touched,
  setStatus,
  email: passedEmail,
  formState,
}: EmailCheckFieldProps) {
  const [content, setContent] = useState('');
  const [isValidEmail, setValidEmail] = useState(false);
  const login = useContext(FormContext);
  const router = useRouter();
  const { track } = useAnalytics();
  const alert = useAlert();
  // Keep referral/city-derived initial values when the user switches auth paths via "Change".
  const preservedResetState = {
    extraInitialValues: formState.extraInitialValues,
  };

  const setEmailAction = async () => {
    if (!isValidEmail) {
      return;
    }
    // The email is unchanged, so neither query would run again on its own after a failed fields load.
    if (signupFieldsError && content === login.state.email) {
      await refetchSignupFields().catch(() => undefined);
      return;
    }
    await login.update({ email: content });
  };

  useEffect(() => {
    if (passedEmail ?? router?.query?.email) {
      const email = passedEmail ?? [].concat(router.query.email).shift();
      setContent(email);
      // Feel we should be able to do this using just setEmailAction, but isValid doesn't seem to update in time.
      schema
        .isValid({
          email,
        })
        .then((valid) => {
          if (valid) {
            login.update({ email });
          }
        });
    }
  }, [router.isReady]);

  const { data, loading: checkingEmail } = useQuery(CHECK_EMAIL, {
    variables: { email: login.state.email },
    skip: !login.state.email,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    schema
      .isValid({
        email: content,
      })
      .then((valid) => {
        setValidEmail(valid);
      });
  }, [content]);

  const {
    data: memberRequirements,
    loading: loadingSignupFields,
    error: signupFieldsError,
    refetch: refetchSignupFields,
  } = useQuery(GET_NEW_MEMBER_FIELDS, {
    variables: { siteId: +process.env.NEXT_PUBLIC_SITE_ID, email: login.state.email },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !data || data?.memberSignInMethods.length,
  });
  const signUpFields = useSuburbPreferenceSignupFields(memberRequirements?.SignUpFields);

  useEffect(() => {
    if (signupFieldsError) {
      alert.error(SIGNUP_FIELDS_ERROR_MESSAGE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupFieldsError]);

  const handleStatusTypes: any = async (memberSignInMethods: string[]) => {
    if (!memberSignInMethods.length) {
      // SignUpFields is still in flight. Publishing now stores an empty list, which the submit button treats as a finished form.
      if (loadingSignupFields || !memberRequirements) {
        return null;
      }
      return {
        active: true,
        method: 'EmailSignUp',
        mutation: CREATE_USER,
        actionLabel: 'Sign Up',
        member: {
          accountStatus: 'INCOMPLETE',
        },
        extraInitialValues: {
          ...formState.extraInitialValues,
          email: login.state.email,
          siteId: +process.env.NEXT_PUBLIC_SITE_ID,
        },
        formFields: transformSignupFields?.(signUpFields) ?? signUpFields,
      };
    }
    // if (memberSignInMethods.includes('EMAIL')) { // Only want to do this if we tell the user they need to login with google/fb/etc
    if (memberSignInMethods.length) {
      // Set focus after delay to allow for rerender - removed due to conflict with validation
      setTimeout(() => document.getElementById('Password')?.focus(), 200);

      return {
        active: true,
        actionLabel: 'Sign In',
        method: 'EmailSignIn',
        mutation: LOGIN_QUERY,
        member: {
          accountStatus: 'INCOMPLETE', // We use this status to prevent the auth firing early
        },
        formFields: [
          {
            id: 'Email',
            type: 'email',
            name: 'email',
            title: 'Email',
            required: true,
            value: login.state.email,
            display: 'none',
          },
          {
            id: 'Password',
            type: 'password',
            name: 'password',
            title: 'Password',
            required: true,
            autoComplete: 'current-password',
          },
          {
            id: 'ForgotPassword',
            type: 'plaintext',
            name: 'forgot',
            title: <ForgotPasswordLink email={login.state.email} />,
          },
        ],
      };
    }
    // If you have FB, Google, Apple, but no password?
  };

  useEffect(() => {
    const handleCheck = async () => {
      if (!data?.memberSignInMethods) {
        return;
      }
      const nextState = await handleStatusTypes(data.memberSignInMethods);
      if (nextState) {
        login.update(nextState);
      }
    };
    handleCheck();
  }, [data, loadingSignupFields, memberRequirements, signUpFields]);

  const onKeyDown = (event: any) => {
    if (['Enter', 'Tab'].includes(event?.key)) {
      event?.preventDefault();
      setEmailAction();
    }
  };

  const onChange = (event: any) => {
    const value = event?.target.value.trim();
    setContent(value);
    if (login.state?.active && content !== value) {
      login.reset(preservedResetState);
    }
  };

  const onBlur = () => {
    setEmailAction();
  };

  const reset = () => {
    setStatus({ touched: [], error: [] });
    login.reset(preservedResetState);
  };

  if (summariseWhenActive && login.state?.active) {
    return (
      <EmailSummary
        className={summaryClassName}
        email={login.state.email}
        label={label?.trim() || undefined}
        onChange={() => {
          // The summary drops the analytics Button, so the back event is reported directly.
          track('button_clicked', {
            button_name: 'back',
            button_type: 'navigation',
            button_copy: 'Change',
          });
          reset();
        }}
      />
    );
  }

  const btn = (props) => {
    if (login.state?.active) {
      return (
        <TrackProvider button_name="back" button_type="navigation">
          <Button size="l" kind="outline" type="reset" onClick={reset} {...props}>
            Change
          </Button>
        </TrackProvider>
      );
    }

    return (
      <TrackProvider button_name="next" button_type="cta">
        <Button
          size="l"
          kind={isValidEmail ? nextButtonKind : 'unavailable'}
          onClick={onBlur}
          isLoading={checkingEmail || loadingSignupFields}>
          Next
        </Button>
      </TrackProvider>
    );
  };

  return (
    <>
      <InputField
        type="email"
        labelSize="default"
        description={null}
        name={name}
        placeholder={(placeholder || '').trim()}
        id={id}
        label={(label || '').trim()}
        size="m"
        error={error}
        success={success}
        borderRadius={2}
        touched={touched}
        onKeyDown={onKeyDown}
        onChange={onChange}
        onBlur={onBlur}
        value={content}
        Button={btn}
        autoComplete="email"
      />
    </>
  );
}
